import { useEffect, useRef } from 'react'
import { createChart, ColorType, HistogramSeries, type IChartApi, type ISeriesApi } from 'lightweight-charts'
import { C, FONT, colHead } from '../theme'
import { usd } from '../lib/format'
import { BlockHead, Chip } from './ui/primitives'
import { usePersistedState } from '../lib/persistedState'
import { useLiveVolume, type VolumePoint } from '../data/liveVolume'

const WINDOWS = [
  { key: 'day', label: '24h', field: 'hourly24h' as const },
  { key: 'month', label: '30d', field: 'daily30d' as const },
  { key: 'all', label: 'all', field: 'dailyAll' as const },
]
type WindowKey = (typeof WINDOWS)[number]['key']

export function VolumeChart() {
  const live = useLiveVolume()
  const [win, setWin] = usePersistedState<WindowKey>('volumeWindow', 'day')

  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const seriesRef = useRef<ISeriesApi<'Histogram'> | null>(null)

  useEffect(() => {
    if (!containerRef.current) return
    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: C.muted2,
        fontFamily: FONT,
        fontSize: 12,
      },
      grid: { vertLines: { visible: false }, horzLines: { color: C.rule } },
      rightPriceScale: { borderColor: C.border },
      timeScale: { borderColor: C.border, timeVisible: true, secondsVisible: false },
    })
    chartRef.current = chart
    seriesRef.current = chart.addSeries(HistogramSeries, {
      color: C.accent,
      priceLineVisible: false,
      lastValueVisible: false,
    })
    return () => {
      chart.remove()
      chartRef.current = null
      seriesRef.current = null
    }
  }, [])

  const points: VolumePoint[] = live ? live[WINDOWS.find((w) => w.key === win)!.field] : []
  const total = points.reduce((n, p) => n + p.value, 0)

  useEffect(() => {
    seriesRef.current?.setData(points.map((p) => ({ time: p.time as never, value: p.value })))
    chartRef.current?.timeScale().fitContent()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points])

  return (
    <div style={{ minWidth: 0, marginTop: 34 }}>
      <BlockHead
        title="On-chain volume"
        right={
          <span style={{ display: 'flex', gap: 4 }}>
            {WINDOWS.map((w) => (
              <Chip key={w.key} active={w.key === win} pad="2px 8px" onClick={() => setWin(w.key)}>
                {w.label}
              </Chip>
            ))}
          </span>
        }
      />
      {!live ? (
        <div style={{ ...colHead, marginTop: 12 }}>simulated — no live chart backend configured</div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginTop: 8 }}>
            <span style={{ fontSize: 26, fontWeight: 700, color: C.accent }}>{usd(total, 0)}</span>
            <span style={{ fontSize: 13, color: C.muted2 }}>
              {points.length ? `${WINDOWS.find((w) => w.key === win)!.label} · settled on chain` : 'no fills in this window yet'}
            </span>
          </div>
        </>
      )}
      <div ref={containerRef} style={{ width: '100%', height: 160, marginTop: 4 }} />
      {live && (
        <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>
          {live.totalFills} fill{live.totalFills === 1 ? '' : 's'} matched since this pipeline started ·{' '}
          {usd(live.totalUsdAllTime, 0)} lifetime · matched by rfq_id against the chain, not a venue report — the
          venue has never once notified a settlement on v2
          {live.byIndex.length > 1 && (
            <>
              {' · by algorithm: '}
              {live.byIndex
                .map((b) => `index ${b.index} ${usd(b.usd, 0)} (${b.fills})`)
                .join(', ')}
            </>
          )}
        </div>
      )}
    </div>
  )
}
